import {
    FileVideoIcon,
    MinusCircleIcon,
    SecurityCameraIcon,
    WarningCircleIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Image } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';
import type { ReportEventDraft } from '@/types/report';
import { SourceType } from '@/types/source';
import { thumbnailFrameUrl } from '@/utils/report-event-thumbnails';

interface SelectedEventRowProps {
    draft: ReportEventDraft;
    isActive: boolean;
    showError: boolean;
    canRemove: boolean;
    onSelect: () => void;
    onRemove: () => void;
}

export function SelectedEventRow({
    draft,
    isActive,
    showError,
    canRemove,
    onSelect,
    onRemove,
}: SelectedEventRowProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const primaryThumbnail = draft.thumbnails[0];
    const SourceIcon =
        draft.event.sourceType === SourceType.FILE
            ? FileVideoIcon
            : SecurityCameraIcon;

    return (
        <div
            className={cn(
                'group flex w-full items-center gap-2 rounded-[4px] p-2 transition-colors',
                isActive ? 'bg-grey-550' : 'hover:bg-surface-hover',
            )}
        >
            <button
                type="button"
                onClick={onSelect}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
            >
                <div className="h-[46px] w-[71px] shrink-0 overflow-hidden rounded-[2px] bg-grey-600">
                    {primaryThumbnail ? (
                        <Image
                            src={thumbnailFrameUrl(
                                draft.event.sourceId,
                                primaryThumbnail,
                            )}
                            alt=""
                            auth
                            generatingPreview
                            className="h-full w-full object-cover"
                        />
                    ) : null}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <p
                        className={cn(
                            'truncate text-sm font-medium',
                            isActive ? 'text-white-100' : 'text-grey-100',
                        )}
                    >
                        {draft.promptText}
                    </p>
                    <span className="flex min-w-0 items-center gap-1 text-xs text-grey-200">
                        <SourceIcon
                            className="h-3.5 w-3.5 shrink-0"
                            aria-hidden
                        />
                        <span className="truncate">
                            {draft.event.sourceName ?? ''}
                        </span>
                    </span>
                </div>
                {showError ? (
                    <WarningCircleIcon
                        className="h-4 w-4 shrink-0 text-error-500"
                        weight="fill"
                    />
                ) : null}
            </button>
            {canRemove ? (
                <button
                    type="button"
                    onClick={onRemove}
                    className={cn(
                        'shrink-0 text-grey-200 transition-colors hover:text-grey-100 focus-visible:opacity-100',
                        isActive
                            ? 'opacity-100'
                            : 'opacity-0 group-hover:opacity-100',
                    )}
                    aria-label={t('addToReport.modal.removeEvent')}
                >
                    <MinusCircleIcon className="h-4 w-4" />
                </button>
            ) : null}
        </div>
    );
}
